import { CherryPractice, ServiceCatalogDiscount, ServiceCatalogService } from '@/components/Timeline/types';
import { fetchBot } from '@/services/services';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';

const BOT_NAME = 'create-treatment-payment';

interface ServiceCatalog {
  services: ServiceCatalogService[];
  discounts: ServiceCatalogDiscount[];
  /** Absent from a bot deployed ahead of the backend change. */
  practices?: CherryPractice[];
  truncated: { services: boolean; discounts: boolean };
}

/**
 * Loads the payments-DB catalog of services and discounts.
 *
 * What actually keeps this from running for users outside PAYMENTS_ROLES is
 * conditional mounting: the drawer is only rendered once the role- and
 * flag-gated Add-menu item is clicked, so reaching this hook already implies
 * permission. `enabled` is belt-and-braces for that — it keeps the fetch tied to
 * the drawer being open rather than to the drawer merely existing, so mounting it
 * unconditionally later cannot silently start calling the bot on every patient
 * view.
 */
export function useTreatmentPaymentCatalog(enabled: boolean): {
  services: ServiceCatalogService[];
  discounts: ServiceCatalogDiscount[];
  practices: CherryPractice[];
  loading: boolean;
  error: string | undefined;
} {
  const medplum = useMedplum();
  const [services, setServices] = useState<ServiceCatalogService[]>([]);
  const [discounts, setDiscounts] = useState<ServiceCatalogDiscount[]>([]);
  const [practices, setPractices] = useState<CherryPractice[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    async function loadCatalog() {
      setLoading(true);
      setError(undefined);

      try {
        const result = await fetchBot<{ catalog: ServiceCatalog }>(medplum, BOT_NAME, {
          operation: 'catalog',
        });

        setServices(result.catalog.services);
        setDiscounts(result.catalog.discounts);
        // `?? []` is load-bearing: a bot deployed before the backend change
        // returns a catalog with no `practices` key.
        setPractices(result.catalog.practices ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load the payment catalog');
        setServices([]);
        setDiscounts([]);
        setPractices([]);
      } finally {
        setLoading(false);
      }
    }

    loadCatalog();
  }, [medplum, enabled]);

  return { services, discounts, practices, loading, error };
}
